import { Check, Trash2, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Modal, ModalFooter } from '@/components/ui/modal'

// 使い方のルール: docs/develop/client_ui/components/modal.md

// 確認ダイアログの共通実装（v1 の Popconfirm / Modal.confirm 相当）。
// 既定は破壊的操作（削除）向けの見た目（destructive アイコン・確定ボタン）。
// 対象外化のような可逆操作は tone="neutral" にして危険な見た目にしない
// （2026/08/26 UX方針決定: 対象外化は削除と違い「対象外を解除」で戻せるため danger 表示にしない）。
// 文言・testid は呼び出し側から渡す。開いている間だけマウントする（Modal と同じ方針）
export function ConfirmDialog({ open, ...props }: ConfirmDialogProps) {
  if (!open) {
    return null
  }
  return <OpenConfirmDialog {...props} />
}

type ConfirmDialogProps = {
  open: boolean
  title: string
  message: ReactNode
  confirmLabel?: string
  confirmTestId?: string
  tone?: 'destructive' | 'neutral'
  /** 見出し左のアイコン。既定は destructive のときのみ Trash2、neutral は呼び出し側で明示する */
  icon?: ReactNode
  /** 確定ボタンの先頭アイコン。既定は destructive のとき Trash2、neutral は Check */
  confirmIcon?: ReactNode
  busy?: boolean
  confirmDisabled?: boolean
  /** sticky 要素内から開く画面では、祖先の stacking context を抜けるため body へ描画する */
  portalToBody?: boolean
  onClose: () => void
  onConfirm: () => void
}

// ref を open 中だけ持つため、マウント中のみの内側コンポーネントに分ける（再度開けばリセットされる）
function OpenConfirmDialog({
  title,
  message,
  confirmLabel = '削除する',
  confirmTestId,
  tone = 'destructive',
  icon,
  confirmIcon,
  busy = false,
  confirmDisabled = false,
  portalToBody = false,
  onClose,
  onConfirm,
}: Omit<ConfirmDialogProps, 'open'>) {
  // 本番で同じネスティング図への削除 mutation が同一秒に12回届いた（経路は未特定。閉じる前の連打・キーリピートを疑う）。
  // 開いている間は最初の1回だけ onConfirm を通す
  const confirmedRef = useRef(false)
  // busy を使う呼び出し元が失敗してダイアログを開いたまま busy を戻したときに、再確定できるようにする
  const prevBusyRef = useRef(busy)
  useEffect(() => {
    if (prevBusyRef.current && !busy) {
      confirmedRef.current = false
    }
    prevBusyRef.current = busy
  }, [busy])
  const handleConfirm = () => {
    if (confirmedRef.current) {
      return
    }
    confirmedRef.current = true
    onConfirm()
  }
  return (
    <Modal
      title={title}
      description={message}
      icon={icon ?? (tone === 'destructive' ? <Trash2 className="size-4" /> : undefined)}
      iconTone={tone === 'destructive' ? 'destructive' : 'primary'}
      portalToBody={portalToBody}
      onClose={busy ? () => undefined : onClose}
    >
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={busy}>
          <X data-icon="inline-start" />
          キャンセル
        </Button>
        <Button
          variant={tone === 'destructive' ? 'destructive' : 'default'}
          data-testid={confirmTestId}
          onClick={handleConfirm}
          disabled={busy || confirmDisabled}
        >
          {confirmIcon ??
            (tone === 'destructive' ? <Trash2 data-icon="inline-start" /> : <Check data-icon="inline-start" />)}
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
